import React, { useState, useEffect, useRef } from "react";

import {MegamenuFeaturedImages} from "../../MegamenuFeaturedImages/MegamenuFeaturedImages";
import {MegamenuLinks} from "../../MegamenuLinks/MegamenuLinks";
import {MegamenuFeaturedContent} from "../../MegamenuFeaturedContent/MegamenuFeaturedContent";
import useMegamenu from "../../hooks/useMegamenu";
import {MegamenuOverlay} from "../../MegamenuOverlay/MegamenuOverlay";
import MegamenuFeaturedCollection from "../../MegamenuFeaturedCollection/MegamenuFeaturedCollection";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";


export const Sidebar = ({
    settings,
    menuItems,
    id,
    layout,
    state
}) => {
    const isMobile = useMobileBreakpoint(settings.mobile_breakpoint || '1024px');
    const megamenuRef = React.useRef(null); 
    const megamenuSM = useMegamenu(megamenuRef, menuItems, false);
    const [collectionBlocks, setCollectionBlocks] = useState({});
    const [headerMenuPosition, setHeaderMenuPosition] = useState(null);
    const level2Layout = settings.section.level2_layout || 'default';

    // Calculate column widths based on settings
    const getColumnWidth = (level) => {
        // Check if there's a specific width for this level
        if (settings.section[`level${level}_width`] && settings.section[`level${level}_width`] > 0) {
            return `${settings.section[`level${level}_width`]}px`;
        }
        
        // Otherwise use general column width if set
        if (settings.section.column_width && settings.section.column_width > 0) {
            return `${settings.section.column_width}%`;
        }
        
        // Default to equal width
        return 'w-1/4';
    };

    const featuredWidth = settings.section.featured_column_width > 0
        ? `${settings.section.featured_column_width}px`
        : 'w-1/4';

    // Set up column gaps
    const columnGaps = {
        columnGap: settings.section.column_gap_x ? `${settings.section.column_gap_x}px` : '0',
        rowGap: settings.section.column_gap_y ? `${settings.section.column_gap_y}px` : '0',
    };

    useEffect(() => {
        const matchingHeaderMenu = document.querySelector(`.header-menu li[data-id="${id}"]`);

        if (matchingHeaderMenu) {
            const rect = matchingHeaderMenu.getBoundingClientRect();
            setHeaderMenuPosition(`${(rect.left + window.scrollX) - settings.section.padding_left_right_desktop - 84 - rect.width}px`);
        }

        const handleMobileMenuClosed = () => megamenuSM.handleMenuClose();
        window.addEventListener("mobile-menu-closed", handleMobileMenuClosed);

        return () => {
            window.removeEventListener("mobile-menu-closed", handleMobileMenuClosed);
        };
    }, [state, id]);

    const overlayBlock = settings.blocks.find(block => block.type === 'overlay');

    useEffect(() => {
        const collections = {}
        settings.blocks?.filter(block => block.type === 'collection')
            ?.forEach((block) => {
                const key = block?.settings?.menu_title;
                collections[key] = block;
            });

        setCollectionBlocks(collections);
    }, []);

    return (
        <>
            {overlayBlock && <MegamenuOverlay settings={overlayBlock} type={'darken'}/>}
            <nav 
                className={`
                    h-screen
                    megamenu-nav
                    megamenu-${layout}
                    ${settings.section.container_type}
                    ${megamenuSM.getPadding(settings.section)}
                `} 
                ref={megamenuRef}
                style={{
                    maxWidth: settings.section.megamenu_width ? `${settings.section.megamenu_width}px` : '100%',
                    paddingLeft: headerMenuPosition
                }}
            >
                <div
                    className={`megamenu-nav__container ${settings.section.container_type_inner} relative overflow-hidden`}
                    style={{
                        borderRadius: `${settings.section.border_radius || 0}px`,
                        height: settings.section.megamenu_height ? `${settings.section.megamenu_height}px` : 'auto'
                    
                    }}
                    data-id={id}
                >
                    <div 
                        className={`megamenu-nav-area flex overflow-auto`}
                        style={columnGaps}
                    >
                        {/* Primary links column */}
                        <div 
                            className={`megamenu-nav-list flex
                                ${settings.section.column_spacing != 'none' ? settings.section.column_spacing : ''}`}
                            style={{ 
                                width: getColumnWidth(1),
                                flexBasis: getColumnWidth(1)
                            }}
                        >
                            <MegamenuLinks settings={settings} megamenuSM={megamenuSM} level={1} />
                        </div>

                        {/* Secondary links column */}
                        {!settings.section.show_featured_collection && (settings.section.show_secondlevel_open_always && megamenuSM.secondLevelLinks?.length > 0) && (
                            <>
                                {(level2Layout === 'grid' && !isMobile)
                                    ?   <div className="py-8 px-6 flex-grow max-w-[664px]">
                                            <MegamenuLinks settings={settings} megamenuSM={megamenuSM} level={2} />
                                        </div>

                                    :   <div 
                                            className={`megamenu-nav-list flex
                                                ${settings.section.column_spacing !== 'none' ? settings.section.column_spacing : ''}`}
                                            style={{ 
                                                width: getColumnWidth(2),
                                                flexBasis: getColumnWidth(2), 
                                                flexGrow: settings.section.column_spacing === 'none' ? 0 : 1 
                                            }}
                                        >
                                            <MegamenuLinks settings={settings} megamenuSM={megamenuSM} level={2} />
                                        </div>
                                }
                            </>
                        )}

                        {/* Featured column - conditional display */}
                        {(settings.section.show_featured_content || 
                          megamenuSM.featuredImageBlocks.length > 0 || 
                          settings.section.show_featured_articles ||
                          megamenuSM.featuredArticles?.length > 0) &&
                          !settings.section.show_featured_collection && (
                            <div 
                                className={`megamenu-nav-list flex`}
                                style={{ 
                                    width: featuredWidth,
                                    flexBasis: featuredWidth,
                                    flexGrow: settings.section.column_spacing === 'none' ? 0 : 1
                                }}
                            >
                                {megamenuSM.featuredImageBlocks.length > 0 ? (
                                    <MegamenuFeaturedImages settings={settings} megamenuSM={megamenuSM} />
                                ) : (
                                    <MegamenuFeaturedContent settings={settings} megamenuSM={megamenuSM} />
                                )}
                            </div>
                        )}

                        {(collectionBlocks[megamenuSM.hoveredLink?.title] && settings.section.show_featured_collection) && (
                            <MegamenuFeaturedCollection
                                settings={settings}
                                collectionBlock={collectionBlocks[megamenuSM.hoveredLink?.title]}
                            />
                        )}
                    </div>
                </div>
            </nav>
        </>
    );
};

export default Sidebar;
